export type Step = "cart" | "substitutions" | "fulfillment" | "when" | "address" | "customer" | "tip" | "donation" | "payment" | "confirm"

export interface StepFlags {
  tableContext: boolean
  donationActive: boolean
  delivery: boolean
  /** Modo "Envío" (Fase 1b). Mutuamente excluyente con `delivery` en la práctica
   *  (un solo `deliveryMode` a la vez), pero la función no asume eso — cualquiera
   *  de los dos basta para insertar `address`. */
  ship: boolean
  /** «Si algo no está disponible» como paso propio, justo después del carrito
   *  (bandera `online_substitutions_enabled`, aprobado por el owner 2026-11-03).
   *  Nunca en mesa por QR. Apagado (o ausente) = la lista de siempre. */
  substitutions?: boolean
}

/**
 * Orden canónico de los pasos del checkout. Antes esto vivía en 4 ternarios inline
 * dentro de OrderSheet (una matriz de flags que crecía en potencias); acá es una
 * lista derivada una vez. `address` solo existe en delivery/ship, y los dos son
 * EXCLUYENTES con tableContext (un cliente sentado por QR no pide a domicilio ni
 * manda su comida por paquetería).
 */
export function buildSteps(f: StepFlags): Step[] {
  const steps: Step[] = ["cart"]
  if (f.substitutions && !f.tableContext) steps.push("substitutions")
  const needsAddress = (f.delivery || f.ship) && !f.tableContext
  if (needsAddress) steps.push("address")
  if (!f.tableContext) steps.push("customer")   // en mesa el cliente ya está identificado
  // Propina para el repartidor (spec 2026-09-12): solo a domicilio. En mesa no
  // hay repartidor y en `ship` va por paquetería. Va ANTES de la donación para
  // que el redondeo de la donación se calcule sobre el total con propina.
  if (f.delivery && !f.tableContext) steps.push("tip")
  if (f.donationActive) steps.push("donation")
  steps.push("payment", "confirm")
  return steps
}

export interface StepFlagsV2 extends StepFlags {
  /** Hay más de una forma de recibir el pedido (recoger / domicilio / envío). */
  hasFulfillmentChoice: boolean
}

/**
 * Checkout por pasos (spec 2026-10-19 §5.2, flag `checkout_steps_v2`): parte el
 * carrito en Carrito → ¿Cómo lo recibes? → ¿Cuándo? y sigue igual que
 * `buildSteps`. En mesa por QR no hay ninguno de los dos pasos nuevos: el
 * resultado es el de `buildSteps`. Qué se SALTA lo decide `skipSetV2`
 * (lib/microsite/checkoutV2.ts); acá solo va el orden.
 */
export function buildStepsV2(f: StepFlagsV2): Step[] {
  const old = buildSteps(f)
  if (f.tableContext) return old
  // `substitutions` (si está) va pegado al carrito, ANTES de ¿Cómo? / ¿Cuándo?.
  const head: Step[] = old[1] === 'substitutions' ? ['cart', 'substitutions'] : ['cart']
  const rest = old.slice(head.length)
  if (f.hasFulfillmentChoice) head.push('fulfillment')
  head.push('when')
  return [...head, ...rest]
}

/** Sin saltos. Un `Set` vacío compartido para no alocar uno por render. */
export const NO_SKIP: ReadonlySet<Step> = new Set()

/**
 * Avanza saltando los pasos en `skip`. El paso saltado SIGUE en `steps`: así
 * "Cambiar dirección" puede entrar a `address` explícitamente y desde ahí
 * Atrás/Siguiente funcionan. Sacarlo de la lista dejaría `indexOf` en -1 y al
 * cliente atrapado (spec 2026-09-03 §4.5).
 */
export function nextStep(current: Step, steps: Step[], skip: ReadonlySet<Step> = NO_SKIP): Step {
  const i = steps.indexOf(current)
  if (i < 0) return current
  for (let j = i + 1; j < steps.length; j++) {
    if (!skip.has(steps[j])) return steps[j]
  }
  return current
}

export function prevStep(current: Step, steps: Step[], skip: ReadonlySet<Step> = NO_SKIP): Step {
  const i = steps.indexOf(current)
  if (i <= 0) return current
  for (let j = i - 1; j >= 0; j--) {
    if (!skip.has(steps[j])) return steps[j]
  }
  return current
}
